import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

import { Menu, X, Phone, Globe, LogIn, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { FrayzeLogo } from "@/components/FrayzeLogo";

export function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Pill nav trigger: a sentinel at the top of the document leaves the viewport
  // -> condense. IntersectionObserver, no scroll listener (design standards).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([entry]) => setIsSticky(!entry.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const solutionLinks = [
    { name: "AI Strategy & Rollout", href: "/solutions/ai-strategy-rollout/" },
    { name: "Website Design", href: "/solutions/websites/" },
    { name: "Local SEO & Reputation", href: "/solutions/local-seo-reputation/" },
    { name: "CRM & Automation", href: "/solutions/crm-automation/" },
    { name: "Lead Generation", href: "/solutions/lead-generation-system/" },
    { name: "Ad Management", href: "/solutions/ad-management-scale/" },
    { name: "For Auto Dealers", href: "/automotive/" },
  ];

  return (
    <>
      {/* in-flow sentinel: while visible, the nav is the full-width bar */}
      <div ref={sentinelRef} aria-hidden className="pointer-events-none absolute top-0 left-0 h-12 w-px" />
    <header className="pointer-events-none fixed top-0 left-0 w-full z-50 transition-all duration-300 font-instrument">
      {/* Utility Bar - Stays on Desktop */}
      <div
        className={cn(
          "pointer-events-auto bg-[#FBFAF7]/50 backdrop-blur-md border-b border-[#E9E6DE]/40 transition-all duration-300 overflow-hidden",
          isSticky ? "h-0 opacity-0" : "h-10 opacity-100"
        )}
      >
        <div className="container mx-auto px-6 h-full flex items-center justify-between text-xs font-semibold text-[#494F5C]">
          <div className="hidden sm:block">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#494F5C] bg-[#F4F2EC] px-3 py-1 rounded-full">
              <Globe className="w-3 h-3 text-[#2F5FE8]" />
              <span>Based in Thunder Bay. Serving businesses across Northern Ontario.</span>
            </div>
          </div>
          <div className="flex items-center gap-6 ml-auto sm:ml-0">
            <a href="tel:+18077000079" className="hover:text-[#2F5FE8] transition-colors flex items-center gap-2">
              <Phone className="h-3 w-3" />
              +1 807-700-0079
            </a>
            <a
              href="https://app.frayze.ca"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#2F5FE8] transition-colors flex items-center gap-2"
            >
              <LogIn className="h-3 w-3" />
              Client Login
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation: full-width bar at top, floating pill once scrolled */}
      <nav
        className={cn(
          "w-full transition-all duration-500",
          isSticky
            ? "px-3 sm:px-5 pt-2"
            : "bg-[#FBFAF7]/50 backdrop-blur-md border-b border-[#E9E6DE]/40"
        )}
      >
        <div
          className={cn(
            "pointer-events-auto mx-auto max-w-[80rem] transition-all duration-500 [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] motion-reduce:transition-none",
            isSticky
              ? "rounded-full border border-[#E9E6DE] bg-[#FBFAF7]/90 backdrop-blur-xl shadow-[0_2px_8px_rgba(16,19,25,0.05),0_12px_32px_-12px_rgba(16,19,25,0.16)] px-4 sm:px-6 py-2"
              : "border border-transparent px-6 py-4"
          )}
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" aria-label="home" className="flex items-center space-x-2 shrink-0">
              <img
                src="/images/frayze-logo-dark.webp"
                alt="Frayze Logo"
                width={384}
                height={115}
                fetchPriority="high"
                className={cn("w-auto object-contain transition-all duration-300", isSticky ? "h-7" : "h-9")}
              />
            </Link>

            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1 xl:gap-2">
              <Link to="/start/" className="inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors text-[#494F5C] hover:text-[#2F5FE8]">
                Start Here
              </Link>

              {/* Solutions Dropdown: pure CSS hover */}
              <div className="relative group">
                <button className="inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors text-[#494F5C] hover:text-[#2F5FE8]">
                  Solutions
                  <ChevronDown className="ml-1 h-3 w-3 transition-transform duration-200 group-hover:rotate-180" />
                </button>
                <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="w-[250px] rounded-lg border border-[#E9E6DE] bg-white shadow-lg p-1.5">
                    {solutionLinks.map((item) => (
                      <Link key={item.href} to={item.href} className="block rounded-md px-3 py-2.5 text-sm font-semibold text-[#494F5C] hover:bg-[#FBFAF7] hover:text-[#2F5FE8] transition-colors">
                        {item.name}
                      </Link>
                    ))}
                    <div className="my-1.5 h-px bg-[#E9E6DE]/60" />
                    <Link to="/solutions/" className="block rounded-md px-3 py-2.5 text-sm font-bold text-[#2F5FE8] hover:bg-[#FBFAF7] transition-colors">
                      All Solutions →
                    </Link>
                  </div>
                </div>
              </div>

              <Link to="/case-studies/" className="inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors text-[#494F5C] hover:text-[#2F5FE8]">
                Results
              </Link>

              <Link to="/pricing/" className="inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors text-[#494F5C] hover:text-[#2F5FE8]">
                Pricing
              </Link>

              <Link to="/resources/" className="inline-flex h-10 items-center justify-center rounded-md px-3 py-2 text-sm font-semibold transition-colors text-[#494F5C] hover:text-[#2F5FE8]">
                Resources
              </Link>

              {/* Singular CTA Button */}
              <Button asChild size="lg" className="ml-4 font-bold bg-[#101319] hover:bg-[#2F5FE8] text-white border border-transparent rounded-xl transition-all shadow-sm hover:scale-[1.02] active:scale-[0.98]">
                <Link to="/book-consultation/">Book a Call</Link>
              </Button>
            </div>

            {/* Mobile Menu Toggle + Essential CTA */}
            <div className="flex items-center gap-4 lg:hidden">
              <Button asChild size="sm" className="text-xs px-4 h-9 font-bold bg-[#101319] hover:bg-[#2F5FE8] text-white border border-transparent rounded-xl transition-all shadow-sm hover:scale-[1.02] active:scale-[0.98]">
                <Link to="/book-consultation/">Book a Call</Link>
              </Button>
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="p-2 -mr-2 text-[#494F5C] hover:text-[#2F5FE8]"
                aria-label={isOpen ? "Close menu" : "Open menu"}
              >
                {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div className="pointer-events-auto fixed inset-0 z-40 bg-[#101319]/40 backdrop-blur-sm lg:hidden animate-in fade-in duration-200" onClick={() => setIsOpen(false)} />
      )}

      {/* Mobile Menu Panel */}
      <div
        className={cn(
          "pointer-events-auto fixed inset-y-0 right-0 z-50 w-full max-w-[320px] bg-[#FBFAF7] border-l border-[#E9E6DE] shadow-2xl p-8 transition-transform duration-300 ease-in-out lg:hidden flex flex-col",
          isOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        <div className="flex items-center justify-between mb-10">
          <img src="/images/frayze-logo-dark.webp" alt="Frayze Logo" width={384} height={115} className="h-7 w-auto object-contain" />
          <button onClick={() => setIsOpen(false)} className="p-2 -mr-2 text-[#494F5C] hover:text-[#2F5FE8]" aria-label="Close menu">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-8 pb-10">
          <div className="space-y-5">
            <Link
              to="/start/"
              className="block text-xl font-bold text-[#101319] hover:text-[#2F5FE8]"
              onClick={() => setIsOpen(false)}
            >
              Start Here
            </Link>

            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#8A8FA0] mb-3">Solutions</p>
              <div className="space-y-3 pl-1">
                {solutionLinks.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="block text-lg font-semibold text-[#494F5C] hover:text-[#2F5FE8]"
                    onClick={() => setIsOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link
                  to="/solutions/"
                  className="block text-lg font-bold text-[#2F5FE8] hover:text-[#2049BE]"
                  onClick={() => setIsOpen(false)}
                >
                  All Solutions →
                </Link>
              </div>
            </div>

            <Link
              to="/case-studies/"
              className="block text-xl font-bold text-[#101319] hover:text-[#2F5FE8]"
              onClick={() => setIsOpen(false)}
            >
              Results
            </Link>

            <Link
              to="/pricing/"
              className="block text-xl font-bold text-[#101319] hover:text-[#2F5FE8]"
              onClick={() => setIsOpen(false)}
            >
              Pricing
            </Link>

            <Link
              to="/resources/"
              className="block text-xl font-bold text-[#101319] hover:text-[#2F5FE8]"
              onClick={() => setIsOpen(false)}
            >
              Resources
            </Link>
          </div>

          <div className="pt-8 border-t border-[#E9E6DE]/60 mt-auto space-y-6">
            <div className="space-y-3">
              <Link
                to="/contact/"
                className="block text-sm font-bold text-[#494F5C] hover:text-[#2F5FE8]"
                onClick={() => setIsOpen(false)}
              >
                Contact Us
              </Link>
              <a
                href="https://app.frayze.ca"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-sm font-bold text-[#494F5C] hover:text-[#2F5FE8]"
                onClick={() => setIsOpen(false)}
              >
                Client Login
              </a>
            </div>

            <Button asChild className="w-full h-14 text-lg font-bold bg-[#101319] hover:bg-[#2F5FE8] text-white rounded-xl border border-transparent shadow-sm transition-all duration-200" size="lg">
              <Link to="/book-consultation/" onClick={() => setIsOpen(false)}>
                Book a Call
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </header>
    </>
  );
}
